// Suppress benign ResizeObserver loop errors emitted by UI libraries.
// These warnings are noisy in development and can trip React's runtime overlay.

const RESIZE_OBSERVER_MESSAGES = [
    'ResizeObserver loop completed with undelivered notifications',
    'ResizeObserver loop limit exceeded',
];

let installCount = 0;
let originalConsoleError = null;
let originalConsoleWarn = null;
let resizeObserverErrorListener = null;

const shouldSuppressResizeObserverMessage = (value) => {
    if (!value) return false;
    const message = String(value);
    return RESIZE_OBSERVER_MESSAGES.some((pattern) => message.includes(pattern));
};

const getEventMessage = (event) => {
    if (!event) return '';
    return (
        event.message ||
        event.error?.message ||
        event.reason?.message ||
        event.reason ||
        ''
    );
};

export const installResizeObserverFix = () => {
    if (typeof window === 'undefined') {
        return () => {};
    }

    if (installCount === 0) {
        originalConsoleError = console.error;
        originalConsoleWarn = console.warn;

        console.error = (...args) => {
            if (args.some(shouldSuppressResizeObserverMessage)) {
                return;
            }
            originalConsoleError.apply(console, args);
        };

        console.warn = (...args) => {
            if (args.some(shouldSuppressResizeObserverMessage)) {
                return;
            }
            originalConsoleWarn.apply(console, args);
        };

        resizeObserverErrorListener = (event) => {
            const message = getEventMessage(event);
            if (!shouldSuppressResizeObserverMessage(message)) {
                return;
            }

            event.stopImmediatePropagation?.();
            event.preventDefault?.();
            return true;
        };

        window.addEventListener('error', resizeObserverErrorListener, true);
    }

    installCount += 1;

    return () => {
        if (installCount <= 0) return;

        installCount -= 1;
        if (installCount > 0) return;

        if (originalConsoleError) {
            console.error = originalConsoleError;
        }

        if (originalConsoleWarn) {
            console.warn = originalConsoleWarn;
        }

        if (resizeObserverErrorListener) {
            window.removeEventListener('error', resizeObserverErrorListener, true);
        }

        originalConsoleError = null;
        originalConsoleWarn = null;
        resizeObserverErrorListener = null;
    };
};

export const fixResizeObserverError = installResizeObserverFix;

export const suppressResizeObserverErrors = installResizeObserverFix;

// React hook for fixing ResizeObserver errors
export const useResizeObserverFix = () => {
    const { useEffect } = require('react');

    useEffect(() => installResizeObserverFix(), []);
};
